// layouts/catalogos/formatos/index.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ListPage from "ui/ListPage";
import ConfirmDialog from "ui/ConfirmDialog";
import SnackbarAlert from "ui/SnackbarAlert";

import { listFormatos, deleteFormato } from "../services/formatos";
import buildTable from "./datosformatos";

const toArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Mapa entre nombre de columna (UI) y campo de datos
 * - "nombre" → name
 * - "creado" → created_at
 */
const SORT_FIELD_MAP = {
  nombre: "name",
  creado: "created_at",
};

export default function Formatos() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [pending, setPending] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const openSnack = (m, s = "success") => setSnack({ open: true, message: m, severity: s });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // paginación client-side
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ordenamiento local
  const [sort, setSort] = useState({
    uiField: "creado",
    direction: "desc",
  });

  // carga inicial
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const list = await listFormatos();
        if (!cancel) setItems(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.message || e.message || String(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // ordenar items según sort
  const sortedItems = useMemo(() => {
    const arr = toArray(items).slice();
    const field = SORT_FIELD_MAP[sort.uiField];

    if (!field) return arr;

    arr.sort((a, b) => {
      const dir = sort.direction === "asc" ? 1 : -1;

      const getVal = (obj) => {
        if (field === "name") {
          return (obj.name ?? obj.nombre ?? "").toString().toLowerCase();
        }
        if (field === "created_at") {
          return obj.created_at ?? obj.createdAt ?? "";
        }
        return obj[field];
      };

      const va = getVal(a);
      const vb = getVal(b);

      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

    return arr;
  }, [items, sort]);

  // construir columnas/filas desde sortedItems
  const { columns, rows } = useMemo(
    () =>
      buildTable(sortedItems, (it) => {
        const id = it.id ?? it._id;
        const nombre = it.name ?? it.nombre ?? "-";
        return {
          id,
          nombre,
          creado: it.created_at ?? it.createdAt,
          onEdit: () => navigate(`/catalogos/formatos/${id}/editar`),
          onDelete: () => setPending({ id, nombre }),
        };
      }),
    [sortedItems, navigate]
  );

  // texto de búsqueda: ahora usa la celda "nombre"
  const getText = (row) =>
    typeof row?.nombre === "string"
      ? row.nombre
      : row?.nombre?.props?.children || "";

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) => String(getText(r)).toLowerCase().includes(s));
  }, [rows, search]);

  // paginación sobre filteredRows
  const pagedRows = useMemo(() => {
    if (!filteredRows.length) return filteredRows;
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleSortChange = (columnName) => {
    if (!SORT_FIELD_MAP[columnName]) return;

    setSort((prev) => {
      let direction = "asc";
      if (prev.uiField === columnName && prev.direction === "asc") {
        direction = "desc";
      }
      return { uiField: columnName, direction };
    });
    setPage(0);
  };

  const confirmDelete = async () => {
    if (!pending) return;
    try {
      await deleteFormato(pending.id);
      setItems((prev) => prev.filter((x) => (x.id ?? x._id) !== pending.id));
      openSnack(`Eliminado: ${pending.nombre}`, "success");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || String(e);
      setErr(msg);
      openSnack(`No se pudo eliminar: ${msg}`, "error");
    } finally {
      setPending(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ListPage
        title="Formatos"
        search={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(0);
        }}
        addText="Agregar"
        onAdd={() => navigate("/catalogos/formatos/nuevo")}
        columns={columns}
        rows={pagedRows}
        loading={loading}
        error={err}
        cardSx={{ maxWidth: "100%", mx: 0 }}
        sortField={sort.uiField}
        sortDirection={sort.direction}
        onSortChange={handleSortChange}
        paginationProps={
          !loading && {
            count: filteredRows.length,
            page,
            rowsPerPage,
            onPageChange: (_e, newPage) => {
              setPage(newPage);
            },
            onRowsPerPageChange: (e) => {
              const newSize = parseInt(e.target.value, 10);
              setRowsPerPage(newSize);
              setPage(0);
            },
            rowsPerPageOptions: [5, 10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }
        }
      />

      <Footer />

      <ConfirmDialog
        open={!!pending}
        title="Eliminar formato"
        description={`¿Seguro que quieres eliminar "${pending?.nombre}"?`}
        confirmText="Eliminar"
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />

      <SnackbarAlert
        open={snack.open}
        onClose={closeSnack}
        message={snack.message}
        severity={snack.severity}
      />
    </DashboardLayout>
  );
}
